/**
 * Vehicle validation schemas (Zod v4) — one schema per wizard step.
 */
import { z } from 'zod'
import { isCoordinateInElSalvador } from '../utils/geo'
import { detectOffPlatformContact } from '../utils/contact-info'
import {
  LISTING_PHOTO_REQUIREMENT_IDS,
  MAX_PHOTOS_PER_REPEATABLE_SLOT,
  MIN_LISTING_PHOTOS,
  isRepeatablePhotoRequirement,
  type ListingPhotoRequirementId,
} from '../types/listing-photo.types'

/**
 * Cap on the host-set refundable deposit (USD 500), mirroring
 * platform_config.max_deposit_cents. Exported so the pricing input clamps to the
 * SAME number the schema enforces: a field that lets you type past the limit and
 * only rejects it at the end is exactly the friction we are removing.
 */
export const MAX_SECURITY_DEPOSIT_CENTS = 50_000

/**
 * Oldest model year Tago accepts. Exported so the year picker can only OFFER
 * years that validate: it used to list 20 years back (2006) while this rejected
 * anything under 2010, so a host with an ordinary 2008 pickup picked a year the
 * app itself proposed and could never leave step 1.
 */
export const MIN_VEHICLE_YEAR = 2010

const currentYear = new Date().getFullYear()

// ─── Step 1: Vehicle Details ────────────────────────────────────────────────

export const vehicleDetailsSchema = z.object({
  makeId: z.string().min(1, 'listing.validation.make_required'),
  modelId: z.string().min(1, 'listing.validation.model_required'),
  year: z
    .number()
    .int()
    .min(MIN_VEHICLE_YEAR, 'listing.validation.year_too_old')
    .max(currentYear + 1, 'listing.validation.year_too_new'),
  category: z.enum(
    [
      'sedan',
      'suv',
      'pickup',
      'van',
      'compact',
      'luxury',
      'sports',
      'motorcycle',
      'microbus',
      'heavy_duty',
      'recreational',
    ],
    {
      message: 'listing.validation.category_required',
    },
  ),
  color: z
    .string()
    .min(2, 'listing.validation.color_required')
    .max(30, 'listing.validation.color_max'),
  transmission: z.enum(['automatic', 'manual', 'cvt', 'dct'], {
    message: 'listing.validation.transmission_required',
  }),
  fuelType: z.enum(['gasoline', 'diesel', 'electric', 'hybrid', 'plugin_hybrid'], {
    message: 'listing.validation.fuel_type_required',
  }),
  seats: z
    .number()
    .int()
    .min(1, 'listing.validation.seats_min')
    .max(15, 'listing.validation.seats_max'),
  doors: z
    .number()
    .int()
    .min(2, 'listing.validation.doors_min')
    .max(6, 'listing.validation.doors_max'),
  plateNumber: z
    .string()
    .min(3, 'listing.validation.plate_required')
    .max(15, 'listing.validation.plate_max')
    .transform((v) => v.toUpperCase().replace(/\s/g, '')),
  plateCountry: z.string().default('SV'),
  vin: z
    .string()
    .length(17, 'listing.validation.vin_length')
    .regex(/^[A-HJ-NPR-Z0-9]+$/, 'listing.validation.vin_format')
    .optional()
    .or(z.literal('')),
  odometerKm: z.number().int().min(0).optional(),
})

export type VehicleDetailsInput = z.infer<typeof vehicleDetailsSchema>

// ─── Step 2: Features ───────────────────────────────────────────────────────

export const vehicleFeaturesSchema = z.object({
  // El título, la descripción y las reglas son texto libre que se publica tal
  // cual: la coordinación va por el chat de Tago, nunca por teléfono, correo o
  // redes (WS-C). Encontrado en vivo: una descripción real con "ORDÉNALA AL
  // 7259-3144". La guarda es de UX al escribir; el servidor sigue siendo la
  // autoridad para el resto del aislamiento de contacto.
  title: z
    .string()
    .min(10, 'listing.validation.title_min')
    .max(80, 'listing.validation.title_max')
    .refine((value) => !detectOffPlatformContact(value), 'listing.validation.no_contact'),
  description: z
    .string()
    .min(30, 'listing.validation.description_min')
    .max(1000, 'listing.validation.description_max')
    .refine((value) => !detectOffPlatformContact(value), 'listing.validation.no_contact')
    .optional()
    .or(z.literal('')),
  features: z.array(z.string()).max(20, 'listing.validation.features_max').default([]),
  rules: z
    .string()
    .max(500, 'listing.validation.rules_max')
    .refine((value) => !detectOffPlatformContact(value), 'listing.validation.no_contact')
    .optional()
    .or(z.literal('')),
  isInstantBook: z.boolean().default(false),
})

export type VehicleFeaturesInput = z.infer<typeof vehicleFeaturesSchema>

// ─── Step 3: Photos ─────────────────────────────────────────────────────────

export const vehiclePhotosSchema = z.object({
  photos: z
    .array(
      z.object({
        uri: z.string().min(1),
        width: z.number().optional(),
        height: z.number().optional(),
        capturedAt: z.string().min(1, 'listing.validation.photos_live_only'),
        source: z.literal('tago_live_camera', {
          message: 'listing.validation.photos_live_only',
        }),
        requirementId: z.enum(LISTING_PHOTO_REQUIREMENT_IDS, {
          message: 'listing.validation.photos_required_all',
        }),
      }),
    )
    // At LEAST one per slot. Accessory slots are repeatable (a single shot cannot
    // show a 360 camera AND the 4x4 selector), so the total may exceed the
    // checklist length — but never fewer, and never an unfilled slot.
    .min(MIN_LISTING_PHOTOS, 'listing.validation.photos_required_all')
    .refine(
      (photos) =>
        LISTING_PHOTO_REQUIREMENT_IDS.every((requirementId) =>
          photos.some((photo) => photo.requirementId === requirementId),
        ),
      { message: 'listing.validation.photos_required_all' },
    )
    .refine(
      (photos) => {
        // Exactly-once for normal slots; up to MAX_PHOTOS_PER_REPEATABLE_SLOT for
        // the repeatable ones.
        const counts = new Map<string, number>()
        for (const photo of photos) {
          counts.set(photo.requirementId, (counts.get(photo.requirementId) ?? 0) + 1)
        }
        return [...counts.entries()].every(([requirementId, count]) =>
          isRepeatablePhotoRequirement(requirementId as ListingPhotoRequirementId)
            ? count <= MAX_PHOTOS_PER_REPEATABLE_SLOT
            : count === 1,
        )
      },
      { message: 'listing.validation.photos_duplicate_slot' },
    ),
})

export type VehiclePhotosInput = z.infer<typeof vehiclePhotosSchema>

// ─── Step 4: Location ───────────────────────────────────────────────────────

export const vehicleLocationSchema = z
  .object({
    homeAddress: z.string().min(5, 'listing.validation.address_required'),
    homeLat: z.number().min(-90).max(90),
    homeLng: z.number().min(-180).max(180),
    countryId: z.string().min(1, 'listing.validation.country_required'),
    cityId: z.string().optional(),
    deliveryRadiusKm: z.number().min(0).max(100).optional(),
  })
  .refine((value) => isCoordinateInElSalvador(value.homeLat, value.homeLng), {
    path: ['homeLat'],
    message: 'listing.validation.location_must_be_sv',
  })

export type VehicleLocationInput = z.infer<typeof vehicleLocationSchema>

// ─── Step 5: Pricing ────────────────────────────────────────────────────────

export const vehiclePricingSchema = z.object({
  dailyRateCents: z
    .number()
    .int()
    .min(1500, 'listing.validation.daily_rate_min') // $15 minimum
    .max(100000, 'listing.validation.daily_rate_max'), // $1000 max
  hourlyRateCents: z.number().int().min(500).max(50000).optional(),
  weeklyDiscountPercent: z.number().min(0).max(50).optional(),
  monthlyDiscountPercent: z.number().min(0).max(70).optional(),
  // Optional host-set security deposit (re-enabled 2026-08-16). When set, the
  // guest is charged it at checkout, Tago holds it, and it is returned to the
  // guest in full after the rental (manual release). Capped at
  // platform_config.max_deposit_cents ($500). 0/undefined = no deposit.
  // Localized bounds: without explicit messages Zod emits its own English text
  // ("Too big: expected number to be <=50000") and the pricing screen renders
  // whatever the schema returns — so a Salvadoran host who typed 600 saw raw
  // English under a Spanish field. The cap is the shared constant the input
  // clamps to, so UI and schema can never disagree.
  securityDepositCents: z
    .number()
    .int()
    .min(0, 'listing.validation.security_deposit_min')
    .max(MAX_SECURITY_DEPOSIT_CENTS, 'listing.validation.security_deposit_max')
    .optional(),
  cleaningFeeCents: z.number().int().min(0).max(10000).optional(),
  dailyKmLimit: z.number().int().min(50).max(1000).optional(),
  excessKmRateCents: z.number().int().min(5).max(200).optional(),
  minRentalHours: z
    .union([z.literal(null), z.literal(48), z.literal(72)], {
      message: 'listing.validation.min_rental_invalid',
    })
    .optional()
    .default(null),
  insuranceModel: z.enum(['platform', 'fleet', 'hybrid', 'none']).default('fleet'),
})

export type VehiclePricingInput = z.infer<typeof vehiclePricingSchema>

// ─── Step 6: Insurance ─────────────────────────────────────────────────────

export const vehicleInsuranceSchema = z.object({
  insuranceModel: z.enum(['fleet'], {
    message: 'listing.validation.insurance_agency_only',
  }),
})

export type VehicleInsuranceInput = z.infer<typeof vehicleInsuranceSchema>

// ─── Available features (for the feature picker) ────────────────────────────

export const VEHICLE_FEATURES = [
  'diesel_included',
  'driver_included',
  'ac',
  'bluetooth',
  'gps',
  'usb',
  'backup_camera',
  'cruise_control',
  'sunroof',
  'heated_seats',
  'leather_seats',
  'keyless_entry',
  'android_auto',
  'apple_carplay',
  'child_seat',
  'roof_rack',
  'tow_hitch',
  'spare_tire',
  'dash_cam',
  'first_aid_kit',
] as const

export type VehicleFeature = (typeof VEHICLE_FEATURES)[number]
